import React, { useEffect, memo, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Clock, Signal, Phone, TrendingUp, Users, BarChart3, RefreshCw, CheckCircle, XCircle, PhoneOff } from 'lucide-react';
import { useTwilioCall } from '@/hooks/useTwilioCall';
import { useCallAwareAnalytics } from '@/hooks/useCallAwareAnalytics';

// Memoized static sections that don't change during calls
const StaticAnalyticsSections = memo(({ 
  todayAnalytics, 
  recentCalls, 
  callInProgress, 
  onRefetch, 
  formatDuration, 
  formatTotalTime, 
  formatDateTime, 
  getQualityColor, 
  getOutcomeIcon 
}: any) => {
  // Early return with default values if data is not loaded
  if (!todayAnalytics || !recentCalls) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-muted rounded-lg"></div>
          <div className="h-48 bg-muted rounded-lg"></div>
          <div className="h-64 bg-muted rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
  <>
    {/* Daily Statistics */}
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Today's Statistics
          </div>
          {!callInProgress && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onRefetch}
              className="h-6 w-6 text-muted-foreground hover:text-foreground"
              title="Refresh statistics"
            >
              <RefreshCw className="h-3 w-3" />
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 p-4">
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <div className="text-center min-w-0">
            <div className="text-lg sm:text-xl font-bold text-primary truncate">{todayAnalytics?.total_calls || 0}</div>
            <div className="text-xs text-muted-foreground">Total Calls</div>
          </div>
          
          <div className="text-center min-w-0">
            <div className="text-lg sm:text-xl font-bold text-green-600 truncate">{todayAnalytics?.success_rate || 0}%</div>
            <div className="text-xs text-muted-foreground">Success Rate</div>
          </div>

          <div className="text-center min-w-0">
            <div className="text-lg sm:text-xl font-bold text-purple-600 truncate">
              ${Math.abs(todayAnalytics?.total_call_price || 0).toFixed(2)}
            </div>
            <div className="text-xs text-muted-foreground">Total Cost</div>
          </div>
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Avg Duration</span>
            <span className="text-sm font-medium">{formatDuration(todayAnalytics?.avg_duration || 0)}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Total Time</span>
            <span className="text-sm font-medium">{formatTotalTime(todayAnalytics?.total_duration || 0)}</span>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Recent Call Performance */}
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <TrendingUp className="h-4 w-4" />
          Recent Calls
        </CardTitle>
      </CardHeader>
      <CardContent>
        {recentCalls && recentCalls.length > 0 ? (
          <div className="space-y-3">
            {recentCalls.map((call: any, index: number) => {
              const dateTime = formatDateTime(call.time);
              const hasValidQuality = call.quality && call.quality !== 'N/A' && call.quality.toLowerCase() !== 'n/a';
              return (
                 <div key={index} className="group hover-scale animate-fade-in p-4 rounded-xl bg-gradient-to-r from-muted/30 to-muted/50 border border-border/50 hover:border-primary/20 transition-all duration-300">
                   <div className="flex items-center justify-between">
                     <div className="flex items-center gap-3">
                       <div className="p-2 rounded-lg bg-background/80 backdrop-blur-sm">
                         {getOutcomeIcon(call.outcome)}
                       </div>
                       <div className="flex flex-col">
                         <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                           <span>{call.phone_number || 'Unknown'}</span>
                           {(call.call_price !== null && call.call_price !== undefined) && (
                             <span className="text-xs font-medium text-purple-600">
                               ${Math.abs(call.call_price).toFixed(2)}
                             </span>
                           )}
                         </div>
                         <div className="flex items-center gap-2 text-xs text-muted-foreground">
                           <span>{dateTime.date}</span>
                           <span>•</span>
                           <span>{dateTime.time}</span>
                           <span>•</span>
                           <span className="font-medium">{call.duration}</span>
                         </div>
                       </div>
                     </div>
                     
                     {hasValidQuality && (
                       <div className="flex items-center gap-2 px-2 py-1 rounded-md bg-background/60">
                         <div className={`w-2 h-2 rounded-full ${getQualityColor(call.quality)}`} />
                         <span className="text-xs font-medium text-muted-foreground">{call.quality}</span>
                       </div>
                     )}
                   </div>
                 </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center text-muted-foreground text-sm py-4">
            No recent calls found
          </div>
        )}
      </CardContent>
    </Card>

    {/* Call Insights */}
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Users className="h-4 w-4" />
          Insights
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
            <div className="text-sm font-medium text-blue-900 dark:text-blue-100">
              Performance Today
            </div>
            <div className="text-xs text-blue-700 dark:text-blue-300 mt-1">
              {todayAnalytics?.total_calls || 0} calls with {todayAnalytics?.success_rate || 0}% success rate
            </div>
          </div>
          
          <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
            <div className="text-sm font-medium text-green-900 dark:text-green-100">
              Call Quality
            </div>
            <div className="text-xs text-green-700 dark:text-green-300 mt-1">
              {todayAnalytics?.call_quality || 0}% average quality today
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  </>
  );
});

const CallAnalyticsComponent = () => {
  const { callInProgress, formattedDuration, callStatus } = useTwilioCall();
  const { todayAnalytics, recentCalls, loading, error, refetch } = useCallAwareAnalytics();
  
  // Only refetch when call truly ends (not on every status change)
  useEffect(() => {
    // Only trigger refresh when transitioning from in-progress to idle
    if (!callInProgress && callStatus === 'idle') {
      console.log('🔄 Call ended, refreshing analytics');
      // Use a timeout to avoid immediate re-render loops
      const timeoutId = setTimeout(() => {
        refetch();
      }, 1000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [callInProgress, callStatus, refetch]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTotalTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getQualityColor = (quality: string) => {
    switch (quality) {
      case 'Excellent': return 'bg-green-500';
      case 'Good': return 'bg-blue-500';
      case 'Poor': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getOutcomeIcon = (outcome: string) => {
    switch (outcome) {
      case 'Connected': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'Voicemail': return <PhoneOff className="h-4 w-4 text-orange-500" />;
      case 'Busy': return <XCircle className="h-4 w-4 text-red-500" />;
      default: return <Phone className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const formatDateTime = (timeString: string) => {
    const now = new Date();
    const callTime = new Date();
    
    // Parse time like "2:30 PM"
    const [time, period] = timeString.split(' ');
    const [hours, minutes] = time.split(':');
    let hour24 = parseInt(hours);
    
    if (period === 'PM' && hour24 !== 12) {
      hour24 += 12;
    } else if (period === 'AM' && hour24 === 12) {
      hour24 = 0;
    }
    
    callTime.setHours(hour24, parseInt(minutes), 0, 0);
    
    // Check if it's today
    const isToday = now.toDateString() === callTime.toDateString();
    
    if (isToday) {
      return {
        date: 'Today',
        time: timeString
      };
    } else {
      return {
        date: callTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        time: timeString
      };
    }
  };

  if (loading) {
    return (
      <ScrollArea className="h-full">
        <div className="p-4 space-y-4">
          <div className="animate-pulse space-y-4">
            <div className="h-32 bg-muted rounded-lg"></div>
            <div className="h-48 bg-muted rounded-lg"></div>
            <div className="h-64 bg-muted rounded-lg"></div>
          </div>
        </div>
      </ScrollArea>
    );
  }

  if (error) {
    return (
      <ScrollArea className="h-full">
        <div className="p-4">
          <div className="text-center text-muted-foreground">
            <p>{error}</p>
            <button 
              onClick={refetch}
              className="mt-2 px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
            >
              Retry
            </button>
          </div>
        </div>
      </ScrollArea>
    );
  }

  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-4">
        {/* Current Call Status */}
        {callInProgress && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Current Call
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Duration</span>
                <Badge variant="outline" className="font-mono">
                  <Clock className="h-3 w-3 mr-1" />
                  {formattedDuration}
                </Badge>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Status</span>
                <Badge variant={callStatus === 'open' ? 'default' : 'secondary'}>
                  {callStatus || 'Unknown'}
                </Badge>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Call Quality</span>
                  <span className="text-sm font-medium">{todayAnalytics.call_quality}%</span>
                </div>
                <Progress value={todayAnalytics.call_quality} className="h-2" />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Signal Strength</span>
                  <span className="text-sm font-medium">87%</span>
                </div>
                <Progress value={87} className="h-2" />
              </div>
            </CardContent>
          </Card>
        )}

        <StaticAnalyticsSections 
          todayAnalytics={todayAnalytics}
          recentCalls={recentCalls}
          callInProgress={callInProgress}
          onRefetch={refetch}
          formatDuration={formatDuration}
          formatTotalTime={formatTotalTime}
          formatDateTime={formatDateTime}
          getQualityColor={getQualityColor}
          getOutcomeIcon={getOutcomeIcon}
        />
      </div>
    </ScrollArea>
  );
};

// Memoize the component to prevent unnecessary re-renders
export const CallAnalytics = memo(CallAnalyticsComponent);
